import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FlyoutMenu from '../flyout-menu/FlyoutMenu';
import postMonetateImpression from '../../../utils/monetate/postMonetateImpression';
import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';
import styles from './DesktopMenu.scss';
import DesktopMenuItem from '../desktop-menu-item/DesktopMenuItem';
import GroupedFlyoutMenu from '../flyout-menu/GroupedFlyoutMenu';
import { isTouchDevice } from '../../../utils/device/identifyDevice';

const DesktopMenu = ({ child = [] }) => {
  const {
    envVars: { monetate },
    features: { monetateExperiences, isBrandPromiseActive },
  } = useContext(EnvVarsFeaturesContext);

  const [activeIndex, setActiveIndex] = useState(-1);

  const filteredChild = child?.filter(
    item => item.isDisplayedInGlobalNav !== false
  );

  const onSelect = (index, label) => () => {
    setActiveIndex(index);

    const monetateTags = [
      { name: 'Home & Garden', tag: 'home-and-garden-ia' },
      { name: 'Furniture & Lights', tag: 'furniture-lights-ia' },
      { name: 'Electricals', tag: 'electricals-ia' },
      { name: 'Women', tag: 'women-ia' },
      { name: 'Men', tag: 'men-ia' },
      { name: 'Beauty', tag: 'beauty-ia' },
      { name: 'Baby & Child', tag: 'baby-child-ia' },
      { name: 'Sport & Leisure', tag: 'sport-leisure-ia' },
    ];

    const { tag = '' } =
      monetateTags.find(monetateTag => monetateTag.name === label) || {};
    if (tag) {
      postMonetateImpression(monetate, tag, monetateExperiences);
    }
  };

  const onClose = () => {
    setActiveIndex(-1);
  };

  const overlayClassName = classNames(styles.desktopMenuOverlay, {
    [styles.desktopMenuOverlay__active]: activeIndex !== -1,
  });

  const isTouch = typeof window !== 'undefined' && isTouchDevice();

  return (
    <nav
      className={classNames(styles.desktopMenu, {
        [styles.desktopMenu__bp]: isBrandPromiseActive,
      })}
      data-testid="navigation-menu"
    >
      <ul className={styles.desktopMenuList} data-testid="navigation-menu-list">
        {filteredChild.map((item, index) => {
          const { contentId, name, url, child: childItems, grouped } = item;
          const onSelectIndex = onSelect(index, name);
          const active = activeIndex === index;
          const Flyout = grouped ? GroupedFlyoutMenu : FlyoutMenu;
          return (
            <DesktopMenuItem
              key={name}
              label={name}
              url={url}
              active={active}
              onClose={onClose}
              onSelect={onSelectIndex}
              isTouch={isTouch}
              isBrandPromiseActive={isBrandPromiseActive}
            >
              {childItems?.length > 0 && (
                <Flyout
                  contentId={contentId}
                  columns={childItems}
                  hierarchy={name}
                  onClose={onClose}
                  isTouch={isTouch}
                  isBrandPromiseActive={isBrandPromiseActive}
                />
              )}
            </DesktopMenuItem>
          );
        })}
      </ul>
      <div className={overlayClassName} data-testid="desktop-menu-overlay" />
    </nav>
  );
};

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

DesktopMenu.propTypes = {
  child: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
};

export default DesktopMenu;
